import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Maximum number of history states to keep
const MAX_HISTORY_SIZE = 50;

type HistoryState = {
  past: any[];           // Stack of past tempProject states
  future: any[];         // Stack of future tempProject states (for redo)
  canUndo: boolean;
  canRedo: boolean;
  lastAction: string;    // Description of the last action (for UI feedback)
};

const initialState: HistoryState = {
  past: [],
  future: [],
  canUndo: false,
  canRedo: false,
  lastAction: '',
};

const historySlice = createSlice({
  name: 'history',
  initialState,
  reducers: {
    // Save current state to history before making a change
    saveToHistory(state, action: PayloadAction<{ tempProject: any; actionName?: string }>) {
      const { tempProject, actionName } = action.payload;
      
      if (!tempProject) return;
      
      // Deep copy the project state
      const stateCopy = JSON.parse(JSON.stringify(tempProject));
      
      // Add to past stack
      state.past.push(stateCopy);
      
      // Limit history size
      if (state.past.length > MAX_HISTORY_SIZE) {
        state.past.shift(); // Remove oldest entry
      }
      
      // Clear future stack when new action is performed
      state.future = [];
      
      // Update flags
      state.canUndo = state.past.length > 0;
      state.canRedo = false;
      state.lastAction = actionName || 'Change';
    },
    
    // Undo: Move current state to future, pop from past
    performUndo(state, action: PayloadAction<{ currentTempProject: any }>) {
      if (state.past.length === 0) return;
      
      const { currentTempProject } = action.payload;
      
      // Save current state to future (for redo)
      if (currentTempProject) {
        state.future.push(JSON.parse(JSON.stringify(currentTempProject)));
      }
      
      // Pop from past (the restored state is handled by the caller BEFORE dispatching this)
      state.past.pop();
      
      // Update flags
      state.canUndo = state.past.length > 0;
      state.canRedo = state.future.length > 0;
      state.lastAction = 'Undo';
    },
    
    // Redo: Move current state to past, pop from future
    performRedo(state, action: PayloadAction<{ currentTempProject: any }>) {
      if (state.future.length === 0) return;
      
      const { currentTempProject } = action.payload;
      
      // Save current state to past
      if (currentTempProject) {
        state.past.push(JSON.parse(JSON.stringify(currentTempProject)));
      }
      
      // Pop from future (the restored state is handled by the caller BEFORE dispatching this)
      state.future.pop();
      
      // Update flags
      state.canUndo = state.past.length > 0;
      state.canRedo = state.future.length > 0;
      state.lastAction = 'Redo';
    },
    
    // Clear all history
    clearHistory(state) {
      state.past = [];
      state.future = [];
      state.canUndo = false;
      state.canRedo = false;
      state.lastAction = '';
    },
  },
});

export const { 
  saveToHistory, 
  performUndo, 
  performRedo, 
  clearHistory,
} = historySlice.actions;

export default historySlice.reducer;

